/**
 * The day entry card (MB's requests of 24 Oct 2026; docs/specs/project-reporting/day-entry-card.md §2, §3):
 *
 * - R40: the card starts with its type, one of the four of the domain (`EntryType`: flight, standby, maintenance,
 *   suspended; mobilisation is a tick on any of them, as in TJR), and shows only that type's fields. A standby,
 *   maintenance or suspended day is the quick entry TJR stored (the 11 Oct 2026 diurnal standby of F26048: no aircraft
 *   crew, positions, hours, fuel or lines; its days allocated, standby type, default block and comments).
 *   `entryForType` clears what the type does not hold, so a hidden field never keeps a value.
 * - R41 and decision 506: the aircraft register settings shape a flight entry. A single has one oil box, an aircraft
 *   whose engine starts are not tracked has no starts box, fuel is typed in the aircraft's unit (stored in litres).
 *   Settings not set yet fall back to the aircraft's TJR record (`engines`, `track_cycles`), then to what the entry
 *   showed before the register (two oil boxes, starts).
 */
import { aircraftFuelType, hoursFlown, maxFlightHours } from './aircraft.js';
import { validateAircraftPerformance } from './aircraft-performance.js';
import { qcFamilyForAircraft } from './geosoft-qc.js';
import type {
  Aircraft,
  Airframe,
  DailyEntry,
  DipstickUnit,
  EntryType,
  FuelUnit,
  ValidationIssue,
} from './model.js';

export interface EntryTypeChoice {
  readonly type: EntryType;
  readonly label: string;
  /** One line under the button. */
  readonly hint: string;
}

/** The types a card starts with, flight first. */
export const ENTRY_TYPE_CHOICES: readonly EntryTypeChoice[] = [
  { type: 'flight', label: 'Flight', hint: 'A flight of the aircraft; import its Zdas log' },
  { type: 'standby', label: 'Standby', hint: 'Diurnal, weather, pilot RDO or another standby day' },
  { type: 'maintenance', label: 'Maintenance', hint: 'A day the aircraft was in maintenance' },
  { type: 'suspended', label: 'Suspended', hint: 'A day the survey was suspended' },
];

export const ENTRY_TYPE_LABEL: Readonly<Record<EntryType, string>> = {
  flight: 'Flight',
  standby: 'Standby',
  maintenance: 'Maintenance',
  suspended: 'Suspended',
};

/** The parts of the card, in order. */
export type EntrySection =
  'log' | 'identity' | 'crew' | 'locations' | 'blocks' | 'hours' | 'fuel' | 'comments';

export interface EntryShape {
  readonly sections: readonly EntrySection[];
  /** "Import flight log" in the card (R42). */
  readonly importLog: boolean;
  readonly flightNumber: boolean;
  readonly takeoff: boolean;
  readonly standbyType: boolean;
  readonly daysAllocated: boolean;
  /** Blocks touched (a flight) beside the default block (every type). */
  readonly blocksTouched: boolean;
  /** Oil boxes: 0 (oil uplift not recorded), 1 (a single), 2 (left and right). */
  readonly oilBoxes: 0 | 1 | 2;
  /** "Dipstick before oil" boxes beside the oil boxes (R150, decision 920; engine-oil.ts): as many as `oilBoxes`. */
  readonly dipstickBoxes: 0 | 1 | 2;
  /** The aircraft register's dipstick unit; null when not set (the boxes then show no unit). */
  readonly dipstickUnit: DipstickUnit | null;
  readonly starts: boolean;
  /** The unit the fuel boxes take. */
  readonly fuelUnit: FuelUnit;
}

const legacy = (a: Aircraft, key: string): string => {
  const v = a.legacy_json[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';
};

/** Engines: the register's, else the TJR record's ("1", "2"), else null. */
export function aircraftEngines(a: Aircraft | null | undefined): 1 | 2 | null {
  if (!a) return null;
  if (a.engines === 1 || a.engines === 2) return a.engines;
  const tjr = legacy(a, 'engines');
  return tjr === '1' ? 1 : tjr === '2' ? 2 : null;
}

/** Whether engine starts are recorded: the register's, else TJR's "track cycles" (-1 yes, 0 no), else yes. */
export function engineStartsTracked(a: Aircraft | null | undefined): boolean {
  if (!a) return true;
  if (a.engine_starts_tracked != null) return a.engine_starts_tracked;
  const tjr = legacy(a, 'track_cycles');
  return tjr === '0' ? false : true;
}

/** The aircraft type: the register's, else from the type name (the Geosoft QC script family). */
export function aircraftAirframe(a: Aircraft | null | undefined): Airframe | null {
  if (!a) return null;
  return a.airframe ?? qcFamilyForAircraft(a.type_name);
}

export const AIRFRAME_LABEL: Readonly<Record<Airframe, string>> = {
  c208: 'Cessna 208',
  c210: 'Cessna 210',
  heli: 'Helicopter',
};

export const FUEL_UNIT_LABEL: Readonly<Record<FuelUnit, string>> = { L: 'L', USG: 'US gal' };

/** Litres in a US gallon. */
export const LITRES_PER_US_GALLON = 3.785411784;

/** Fuel typed in `unit` as whole litres (how Teslyn stores it). */
export function litresFromFuel(amount: number, unit: FuelUnit): number {
  return Math.round(unit === 'USG' ? amount * LITRES_PER_US_GALLON : amount);
}

/** Stored litres in `unit` (US gallons unrounded; the screen shows one decimal). */
export function fuelFromLitres(litres: number, unit: FuelUnit): number {
  return unit === 'USG' ? litres / LITRES_PER_US_GALLON : litres;
}

const FLIGHT_SECTIONS: readonly EntrySection[] = [
  'log',
  'identity',
  'crew',
  'locations',
  'blocks',
  'hours',
  'fuel',
  'comments',
];
const DAY_SECTIONS: readonly EntrySection[] = ['identity', 'blocks', 'comments'];

/** What the card of `type` shows, for `aircraft` (null: none chosen yet). */
export function entryShape(type: EntryType, aircraft?: Aircraft | null): EntryShape {
  const flight = type === 'flight';
  const engines = aircraftEngines(aircraft);
  const oil = aircraft?.oil_uplift_tracked === false ? 0 : engines === 1 ? 1 : 2;
  return {
    sections: flight ? FLIGHT_SECTIONS : DAY_SECTIONS,
    importLog: flight,
    flightNumber: flight,
    takeoff: flight,
    standbyType: type === 'standby',
    daysAllocated: !flight,
    blocksTouched: flight,
    oilBoxes: flight ? oil : 0,
    dipstickBoxes: flight ? oil : 0,
    dipstickUnit: aircraft?.oil_dipstick_unit ?? null,
    starts: flight && engineStartsTracked(aircraft),
    fuelUnit: aircraft?.fuel_unit ?? 'L',
  };
}

/**
 * The entry as a card of `type` holds it: the fields the type (and the aircraft's settings) does not show are
 * cleared, so a standby day never keeps a flight's hours and a single never keeps a right-engine oil figure.
 */
export function entryForType<E extends DailyEntry>(entry: E, type: EntryType, aircraft?: Aircraft | null): E {
  const next: { -readonly [K in keyof E]: E[K] } = { ...entry, entry_type: type };
  if (type !== 'standby') next.standby_type = null;
  if (type !== 'flight') {
    Object.assign(next, {
      // TJR stored 0 for "no flight number" on its standby days; Teslyn keeps that as it is
      flt_num: entry.flt_num === 0 ? 0 : null,
      takeoff_time: null,
      pilot_id: null,
      pilot2_id: null,
      operator1_id: null,
      operator2_id: null,
      base_mag_position_id: null,
      ground_cal_position_id: null,
      test_line_position_id: null,
      blocks: [],
      svy_hours: 0,
      cbx_hours: 0,
      cal_hours: 0,
      fry_hours: 0,
      ext_hours: 0,
      fuel_used_l: 0,
      fuel_source: null,
      fuel_location: '',
      fuel_load_l: 0,
      starts: 0,
      oil_l: 0,
      oil_r: 0,
      ...dipsticksCleared(entry),
      new_mr_ttis_h: null,
      log_file: null,
    });
    return next;
  }
  const shape = entryShape('flight', aircraft);
  if (aircraft) {
    if (shape.oilBoxes === 0) Object.assign(next, { oil_l: 0, oil_r: 0, ...dipsticksCleared(entry) });
    if (shape.oilBoxes === 1) {
      next.oil_r = 0;
      if (entry.oil_dipstick_r !== undefined) next.oil_dipstick_r = null;
    }
    if (!shape.starts) next.starts = 0;
  }
  return next;
}

/** The dipstick readings emptied (R150), naming only the fields the entry has (an entry read before 0071 has none). */
function dipsticksCleared(entry: DailyEntry): Partial<Pick<DailyEntry, 'oil_dipstick_l' | 'oil_dipstick_r'>> {
  const out: { oil_dipstick_l?: null; oil_dipstick_r?: null } = {};
  if (entry.oil_dipstick_l !== undefined) out.oil_dipstick_l = null;
  if (entry.oil_dipstick_r !== undefined) out.oil_dipstick_r = null;
  return out;
}

// ---------------------------------------------------------------- a new flight entry's defaults (R111, R116)

const flightOrder = (e: Pick<DailyEntry, 'date' | 'flt_num'>): number => e.flt_num ?? 0;

/**
 * The flight before `entry` (MB's R116): the same aircraft's latest flight entry earlier than it, by date and then
 * flight number (an entry with no flight number yet follows that day's flights). Deleted entries, other aircraft, days
 * that are not flights and the entry itself never count; null when there is none (or no aircraft is chosen).
 */
export function previousFlightEntry<E extends DailyEntry>(
  entries: readonly E[],
  entry: Pick<DailyEntry, 'id' | 'aircraft_id' | 'date' | 'flt_num'>,
): E | null {
  if (!entry.aircraft_id) return null;
  const earlier = (e: E): boolean =>
    e.date < entry.date ||
    (e.date === entry.date && (entry.flt_num === null || flightOrder(e) < entry.flt_num));
  const before = entries
    .filter(
      (e) =>
        e.deleted_at === null &&
        e.id !== entry.id &&
        e.entry_type === 'flight' &&
        e.aircraft_id === entry.aircraft_id &&
        earlier(e),
    )
    .sort((a, b) => a.date.localeCompare(b.date) || flightOrder(a) - flightOrder(b));
  return before[before.length - 1] ?? null;
}

export interface NewFlightContext {
  /** The entry's aircraft (null: none chosen yet). */
  readonly aircraft: Aircraft | null;
  /** The project's daily entries. */
  readonly entries: readonly DailyEntry[];
  /** The fields the person has typed or chosen on this card: they keep what was typed. */
  readonly changed: ReadonlySet<keyof DailyEntry>;
}

/**
 * A new flight entry's defaults, for each field the person has not changed (MB, 27 Oct 2026), worked out again when
 * the aircraft, date or flight number changes:
 * - R111: Starts is 1 when the aircraft logs engine starts (its register setting, R41; `engineStartsTracked`), else 0;
 * - R116: the fuel location is the previous flight's (`previousFlightEntry`), blank when there is none.
 * Any other entry type is returned as it is.
 */
export function newFlightEntryDefaults<E extends DailyEntry>(entry: E, ctx: NewFlightContext): E {
  if (entry.entry_type !== 'flight') return entry;
  const next: { -readonly [K in keyof E]: E[K] } = { ...entry };
  if (!ctx.changed.has('starts')) next.starts = ctx.aircraft && engineStartsTracked(ctx.aircraft) ? 1 : 0;
  if (!ctx.changed.has('fuel_location'))
    next.fuel_location = previousFlightEntry(ctx.entries, entry)?.fuel_location ?? '';
  return next;
}

/** What changing the entry to `type` would clear, in words (asked before the change). */
export function typeChangeLoses(entry: DailyEntry, type: EntryType): string[] {
  if (type === entry.entry_type) return [];
  const out: string[] = [];
  if (type !== 'standby' && entry.standby_type !== null) out.push('the standby type');
  if (type === 'flight') return out;
  if (entry.flt_num !== null && entry.flt_num > 0) out.push(`flight number ${entry.flt_num}`);
  if (entry.takeoff_time) out.push(`take-off ${entry.takeoff_time}`);
  const h = hoursFlown(entry) + entry.ext_hours;
  if (h > 0) out.push(`${h.toFixed(1)} h of flight hours`);
  if ([entry.pilot_id, entry.pilot2_id, entry.operator1_id, entry.operator2_id].some((x) => x !== null))
    out.push('the crew');
  if (
    [entry.base_mag_position_id, entry.ground_cal_position_id, entry.test_line_position_id].some(
      (x) => x !== null,
    )
  )
    out.push('the locations');
  if (entry.blocks.length > 0) out.push('the blocks touched');
  if (entry.fuel_used_l > 0) out.push(`fuel used ${entry.fuel_used_l} L`);
  if (entry.fuel_load_l > 0) out.push(`refuel ${entry.fuel_load_l} L`);
  if (entry.oil_l + entry.oil_r > 0) out.push('the oil added');
  if ((entry.oil_dipstick_l ?? null) !== null || (entry.oil_dipstick_r ?? null) !== null)
    out.push('the dipstick readings');
  if (entry.starts > 0) out.push('the engine starts');
  if (entry.new_mr_ttis_h !== null) out.push('the new MR TTIS');
  return out;
}

// ---------------------------------------------------------------- the register settings (decision 506)

/** A new or changed aircraft's register settings. */
export function validateAircraftSettings(a: Aircraft): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (a.engines != null && a.engines !== 1 && a.engines !== 2)
    issues.push({ field: 'engines', message: 'Choose 1 or 2 engines.' });
  if (a.fuel_unit != null && a.fuel_unit !== 'L' && a.fuel_unit !== 'USG')
    issues.push({ field: 'fuel_unit', message: 'Choose litres or US gallons.' });
  if (a.oil_dipstick_unit != null && !['qt', 'L', 'cm', 'mm', 'in'].includes(a.oil_dipstick_unit))
    issues.push({ field: 'oil_dipstick_unit', message: 'Choose qt, L, cm, mm or in for the dipstick.' });
  if (a.airframe != null && !(a.airframe in AIRFRAME_LABEL))
    issues.push({ field: 'airframe', message: 'Choose Cessna 208, Cessna 210 or Helicopter.' });
  if (a.max_flight_h != null && !(a.max_flight_h > 0))
    issues.push({ field: 'max_flight_h', message: 'Maximum endurance must be more than 0 h.' });
  if (a.fuel_flow_lph != null && !(a.fuel_flow_lph > 0))
    issues.push({ field: 'fuel_flow_lph', message: 'Nominal fuel burn must be more than 0 L/h.' });
  if (a.mr_interval_h != null && !(a.mr_interval_h > 0))
    issues.push({ field: 'mr_interval_h', message: 'MR hours must be more than 0 h.' });
  // the JSA's empty weight, MTOW and reserve (decision 863, 6 B)
  issues.push(...validateAircraftPerformance(a));
  return issues;
}

/** Sign-in roles that change the register settings (decision 506; the database's `aircraft_register_guard`). */
export const AIRCRAFT_REGISTER_EDITORS = ['tenant_admin', 'manager'] as const;

/**
 * May this person change the register settings? A project kept on this laptop alone lets anyone; otherwise tenant
 * admins and managers ('read' for the others, 'unknown' while the roles are not known on this laptop).
 */
export function aircraftRegisterAccess(
  roles: readonly string[] | null,
  localWorkspace: boolean,
): 'edit' | 'read' | 'unknown' {
  if (localWorkspace) return 'edit';
  if (roles === null) return 'unknown';
  return roles.some((r) => (AIRCRAFT_REGISTER_EDITORS as readonly string[]).includes(r)) ? 'edit' : 'read';
}

/**
 * The aircraft's older settings (fuel type, MR hours, maximum endurance, nominal fuel burn) stay open to every staff
 * member (decision 537, Q475 point 4 B, 25 Oct 2026): only the five settings of batch 3 (type, engines, starts, oil,
 * fuel unit), the dipstick unit (decision 920) and the tracker device are for tenant admins and managers
 * ({@link aircraftRegisterAccess}).
 */
export const AIRCRAFT_OPEN_SETTINGS = [
  'fuel_type',
  'mr_interval_h',
  'max_flight_h',
  'fuel_flow_lph',
  // the JSA's figures kept in the register (decision 863, 6 B), like the fuel burn
  'empty_weight_kg',
  'mtow_kg',
  'reserve_min',
] as const;

const STAFF = ['tenant_admin', 'manager', 'field_crew', 'office_staff', 'data_processor'];

/** May this person change the aircraft's older settings ({@link AIRCRAFT_OPEN_SETTINGS})? Every staff role may. */
export function aircraftOpenSettingsAccess(
  roles: readonly string[] | null,
  localWorkspace: boolean,
): 'edit' | 'read' | 'unknown' {
  if (localWorkspace) return 'edit';
  if (roles === null) return 'unknown';
  return roles.some((r) => STAFF.includes(r)) ? 'edit' : 'read';
}

/** The register settings of an aircraft in words, for the card and the register list. */
export function aircraftSettingsWords(a: Aircraft): string {
  const parts: string[] = [];
  const type = aircraftAirframe(a);
  if (type) parts.push(AIRFRAME_LABEL[type]);
  const engines = aircraftEngines(a);
  if (engines) parts.push(engines === 1 ? 'single engine' : 'twin engine');
  parts.push(engineStartsTracked(a) ? 'engine starts recorded' : 'engine starts not recorded');
  if (a.oil_uplift_tracked === false) parts.push('oil uplift not recorded');
  else if (a.oil_dipstick_unit) parts.push(`dipstick in ${a.oil_dipstick_unit}`);
  const fuel = aircraftFuelType(a);
  if (fuel) parts.push(fuel === 'turbine' ? 'Jet A-1' : 'Avgas');
  parts.push(`fuel in ${FUEL_UNIT_LABEL[a.fuel_unit ?? 'L']}`);
  const max = maxFlightHours(a);
  if (max !== null) parts.push(`endurance ${max.toFixed(1)} h`);
  if (a.fuel_flow_lph) parts.push(`${a.fuel_flow_lph} L/h`);
  // the JSA's figures (decision 863, 6 B)
  if (a.empty_weight_kg != null) parts.push(`empty ${a.empty_weight_kg} kg`);
  if (a.mtow_kg != null) parts.push(`MTOW ${a.mtow_kg} kg`);
  if (a.reserve_min != null) parts.push(`reserve ${a.reserve_min} min`);
  // R184: the register's tick
  if (a.uses_flight_watch === true) parts.push('V2Track flight watch alerts');
  return parts.join(', ').replace(/^./, (c) => c.toUpperCase());
}

/** Planned flights longer than an aircraft's maximum endurance (decision 506), in words. */
export function enduranceWarnings(
  flights: ReadonlyArray<{ readonly flight_num: number; readonly total_time_hrs: number }>,
  aircraft: readonly Aircraft[],
): string[] {
  const out: string[] = [];
  for (const f of flights)
    for (const a of aircraft) {
      const max = a.deleted_at ? null : maxFlightHours(a);
      if (max !== null && f.total_time_hrs > max + 1e-9)
        out.push(
          `Flight ${f.flight_num} is planned at ${f.total_time_hrs.toFixed(1)} h, longer than the maximum endurance of ${a.registration} (${max.toFixed(1)} h).`,
        );
    }
  return out;
}
